import InfoIcon from "@mui/icons-material/ArrowOutward";
import ActionIcon from "@mui/icons-material/AutoAwesome";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import ReasonIcon from "@mui/icons-material/Psychology";
import QuestionIcon from "@mui/icons-material/QuestionMark";
import WarnIcon from "@mui/icons-material/WarningAmber";
import {
  alpha,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { STATUS_COLORS } from "@/theme/theme";
import { useApiQuery } from "../hooks";
import type { DiscoveryRunLog, DiscoveryRunLogLevel } from "../types";

type Props = {
  runId: string;
};

export const DiscoveryRunLogs = ({ runId }: Props) => {
  const theme = useTheme();
  const { data, isLoading } = useApiQuery<DiscoveryRunLog[]>(`/discovery-runs/${runId}/logs`, {
    enabled: !!runId,
  });

  const getColorForLogLevel = (level: DiscoveryRunLogLevel) => {
    switch (level) {
      case "info":
        return theme.palette.info.main;
      case "question":
      case "warning":
        return theme.palette.warning.main;
      case "error":
        return theme.palette.error.main;
      case "reasoning":
        return theme.palette.success.main;
      case "action":
        return STATUS_COLORS.purple.border;
    }
  };

  const getLogLevelIcon = (level: DiscoveryRunLogLevel) => {
    switch (level) {
      case "info":
        return <InfoIcon color="info" fontSize="small" />;
      case "warning":
        return <WarnIcon color="warning" fontSize="small" />;
      case "error":
        return <ErrorIcon color="error" fontSize="small" />;
      case "reasoning":
        return <ReasonIcon color="success" fontSize="small" />;
      case "question":
        return <QuestionIcon color="warning" fontSize="small" />;
      case "action":
        return (
          <ActionIcon
            color="inherit"
            fontSize="small"
            sx={{ color: STATUS_COLORS.purple.border }}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={1} sx={{ padding: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading logs...
        </Typography>
      </Stack>
    );
  }

  if (!data) {
    return (
      <Stack spacing={1} sx={{ padding: 2 }}>
        <Typography variant="body2" color="error">
          Failed to load logs.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        padding: 0,
        maxHeight: "calc(100vh - 220px)",
        overflowY: "auto",
      }}
    >
      <Stack
        spacing={2}
        data-testid="discovery-run-logs"
        direction="column"
        width="100%"
        alignItems="stretch"
        justifyContent="flex-start"
      >
        {data.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No logs available for this run.
          </Typography>
        )}
        {data.map((log) => (
          <Card
            key={log.id}
            sx={{
              width: "100%",
              backgroundColor: alpha(getColorForLogLevel(log.level), 0.05),
              borderColor: alpha(getColorForLogLevel(log.level), 0.2),
            }}
          >
            <CardContent>
              <Stack
                direction="column"
                alignItems="stretch"
                spacing={1}
                divider={
                  <Divider
                    sx={{
                      backgroundColor: alpha(getColorForLogLevel(log.level), 0.2),
                    }}
                  />
                }
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {getLogLevelIcon(log.level)}
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(log.timestamp), "yyyy-MM-dd HH:mm:ss")}
                  </Typography>
                </Stack>
                <Stack direction="column" spacing={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {log.message}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};
